import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Terminal Code Card with live typing animation
export const TerminalCard = () => {
    const [cursorPos, setCursorPos] = useState(0);
    const [currentLine, setCurrentLine] = useState(0);

    const codeLines = [
        { text: 'const EchoMedia = new Agency({', colored: true },
        { text: "  services: ['Web', 'Mobile', 'AI'],", colored: true },
        { text: '  quality: "Premium",', colored: true },
        { text: '  delivery: true', colored: true },
        { text: '})', colored: true },
        { text: '// Your vision, our code', isComment: true },
    ];

    const totalChars = codeLines.reduce((acc, line) => acc + line.text.length, 0);

    useEffect(() => {
        let charCount = 0;
        let lineIdx = 0;
        let timeout;

        const animate = () => {
            charCount++;

            // Calculate which line and position
            let remaining = charCount;
            lineIdx = 0;
            for (let i = 0; i < codeLines.length; i++) {
                if (remaining <= codeLines[i].text.length) {
                    lineIdx = i;
                    break;
                }
                remaining -= codeLines[i].text.length;
                lineIdx = i + 1;
            }

            setCursorPos(charCount);
            setCurrentLine(lineIdx);

            if (charCount >= totalChars) {
                // Reset after delay
                timeout = setTimeout(() => {
                    charCount = 0;
                    setCursorPos(0);
                    setCurrentLine(0);
                    animate();
                }, 2000);
            } else {
                timeout = setTimeout(animate, 50);
            }
        };

        animate();
        return () => clearTimeout(timeout);
    }, [totalChars]);

    // Helper to render line with cursor
    const renderLine = (line, lineIdx) => {
        // Calculate chars before this line
        let charsBefore = 0;
        for (let i = 0; i < lineIdx; i++) {
            charsBefore += codeLines[i].text.length;
        }

        const lineStart = charsBefore;
        const lineEnd = charsBefore + line.text.length;
        const cursorInLine = cursorPos > lineStart && cursorPos <= lineEnd;
        const cursorPosInLine = cursorPos - lineStart;
        const lineComplete = cursorPos >= lineEnd;

        if (line.isComment) {
            return (
                <div className="text-slate-600 pt-2">
                    {lineComplete ? line.text : line.text.slice(0, Math.max(0, cursorPosInLine))}
                    {cursorInLine && <span className="text-cyan-400 animate-pulse">|</span>}
                </div>
            );
        }

        const visibleText = lineComplete ? line.text : line.text.slice(0, Math.max(0, cursorPosInLine));

        return (
            <div className={lineIdx > 0 && lineIdx < 5 ? 'pl-4' : ''}>
                <span>
                    {colorizeCode(visibleText, line.text)}
                </span>
                {cursorInLine && <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ duration: 0.5, repeat: Infinity }} className="text-cyan-400">|</motion.span>}
            </div>
        );
    };

    const colorizeCode = (visible, full) => {
        return visible.split('').map((char, i) => {
            // Keywords
            if (full.startsWith('const') && i < 5) return <span key={i} className="text-purple-400">{char}</span>;
            if (full.includes('new') && full.indexOf('new') <= i && i < full.indexOf('new') + 3) return <span key={i} className="text-yellow-400">{char}</span>;
            if (full.includes('Agency') && full.indexOf('Agency') <= i && i < full.indexOf('Agency') + 6) return <span key={i} className="text-green-400">{char}</span>;
            if (full.includes('EchoMedia') && full.indexOf('EchoMedia') <= i && i < full.indexOf('EchoMedia') + 9) return <span key={i} className="text-blue-400">{char}</span>;
            // Properties
            if (full.includes('services') && full.indexOf('services') <= i && i < full.indexOf('services') + 8) return <span key={i} className="text-cyan-400">{char}</span>;
            if (full.includes('quality') && full.indexOf('quality') <= i && i < full.indexOf('quality') + 7) return <span key={i} className="text-cyan-400">{char}</span>;
            if (full.includes('delivery') && full.indexOf('delivery') <= i && i < full.indexOf('delivery') + 8) return <span key={i} className="text-cyan-400">{char}</span>;
            // Values
            if (char === "'" || char === '"') return <span key={i} className="text-orange-300">{char}</span>;
            if (full.includes("'Web'") && full.indexOf('Web') <= i && i < full.indexOf('Web') + 3) return <span key={i} className="text-orange-300">{char}</span>;
            if (full.includes("'Mobile'") && full.indexOf('Mobile') <= i && i < full.indexOf('Mobile') + 6) return <span key={i} className="text-orange-300">{char}</span>;
            if (full.includes("'AI'") && full.indexOf('AI') <= i && i < full.indexOf('AI') + 2) return <span key={i} className="text-orange-300">{char}</span>;
            if (full.includes('"Premium"') && full.indexOf('Premium') <= i && i < full.indexOf('Premium') + 7) return <span key={i} className="text-green-300">{char}</span>;
            if (full.includes('true') && full.indexOf('true') <= i && i < full.indexOf('true') + 4) return <span key={i} className="text-green-400">{char}</span>;
            // Punctuation
            if (char === ':' || char === ',' || char === '[' || char === ']' || char === '{' || char === '}' || char === '(' || char === ')') return <span key={i} className="text-slate-400">{char}</span>;
            return <span key={i} className="text-slate-300">{char}</span>;
        });
    };

    return (
        <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-8 right-0 w-[400px] bg-[#0d0d0f]/95 backdrop-blur-2xl border border-slate-700/50 rounded-2xl shadow-2xl shadow-cyan-500/5 overflow-hidden"
        >
            <div className="flex items-center justify-between px-4 py-3 bg-slate-800/50 border-b border-slate-700/50">
                <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <span className="text-slate-500 text-xs font-mono">echo-media.js</span>
                <div className="w-16" />
            </div>
            <div className="p-5 font-mono text-sm space-y-1 min-h-[180px]">
                {codeLines.map((line, idx) => (
                    <div key={idx}>{renderLine(line, idx)}</div>
                ))}
            </div>
        </motion.div>
    );
};

// Component Card with live typing animation
export const ComponentCard = () => {
    const [cursorPos, setCursorPos] = useState(0);

    const codeLines = [
        'export const Success = () => {',
        '  return (',
        '    <Project',
        '      client="Happy"',
        '      status="Delivered"',
        '    />',
        '  )',
        '}',
    ];

    const totalChars = codeLines.reduce((acc, line) => acc + line.length, 0);

    useEffect(() => {
        let charCount = 0;
        let timeout;

        const startDelay = setTimeout(() => {
            const animate = () => {
                charCount++;
                setCursorPos(charCount);

                if (charCount >= totalChars) {
                    timeout = setTimeout(() => {
                        charCount = 0;
                        setCursorPos(0);
                        animate();
                    }, 2500);
                } else {
                    timeout = setTimeout(animate, 40);
                }
            };
            animate();
        }, 2000);

        return () => {
            clearTimeout(startDelay);
            clearTimeout(timeout);
        };
    }, [totalChars]);

    const colorizeJSX = (char, fullLine, i) => {
        if (fullLine.startsWith('export') && i < 6) return <span key={i} className="text-purple-400">{char}</span>;
        if (fullLine.includes('const') && fullLine.indexOf('const') <= i && i < fullLine.indexOf('const') + 5) return <span key={i} className="text-purple-400">{char}</span>;
        if (fullLine.includes('return') && fullLine.indexOf('return') <= i && i < fullLine.indexOf('return') + 6) return <span key={i} className="text-purple-400">{char}</span>;
        if (fullLine.includes('Success') && fullLine.indexOf('Success') <= i && i < fullLine.indexOf('Success') + 7) return <span key={i} className="text-yellow-400">{char}</span>;
        if (fullLine.includes('Project') && fullLine.indexOf('Project') <= i && i < fullLine.indexOf('Project') + 7) return <span key={i} className="text-green-400">{char}</span>;
        if (fullLine.includes('client') && fullLine.indexOf('client') <= i && i < fullLine.indexOf('client') + 6) return <span key={i} className="text-cyan-400">{char}</span>;
        if (fullLine.includes('status') && fullLine.indexOf('status') <= i && i < fullLine.indexOf('status') + 6) return <span key={i} className="text-cyan-400">{char}</span>;
        if (char === '"') return <span key={i} className="text-orange-300">{char}</span>;
        if (fullLine.includes('Happy') && fullLine.indexOf('Happy') <= i && i < fullLine.indexOf('Happy') + 5) return <span key={i} className="text-orange-300">{char}</span>;
        if (fullLine.includes('Delivered') && fullLine.indexOf('Delivered') <= i && i < fullLine.indexOf('Delivered') + 9) return <span key={i} className="text-orange-300">{char}</span>;
        if (char === '<' || char === '>' || char === '/') return <span key={i} className="text-slate-500">{char}</span>;
        if (char === '=' || char === '(' || char === ')' || char === '{' || char === '}') return <span key={i} className="text-slate-400">{char}</span>;
        return <span key={i} className="text-slate-300">{char}</span>;
    };

    const renderLines = () => {
        let charsSoFar = 0;
        return codeLines.map((line, lineIdx) => {
            const lineStart = charsSoFar;
            const lineEnd = charsSoFar + line.length;
            charsSoFar = lineEnd;

            const cursorInLine = cursorPos > lineStart && cursorPos <= lineEnd;
            const cursorPosInLine = cursorPos - lineStart;
            const visibleText = cursorPos >= lineEnd ? line : line.slice(0, Math.max(0, cursorPosInLine));

            return (
                <div key={lineIdx}>
                    {visibleText.split('').map((char, i) => colorizeJSX(char, line, i))}
                    {cursorInLine && <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ duration: 0.5, repeat: Infinity }} className="text-cyan-400">|</motion.span>}
                </div>
            );
        });
    };

    return (
        <motion.div
            animate={{ y: [0, 15, 0], x: [0, -5, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-40 left-0 w-[320px] bg-[#0d0d0f]/95 backdrop-blur-2xl border border-cyan-500/20 rounded-2xl shadow-xl shadow-cyan-500/10 overflow-hidden"
        >
            <div className="flex items-center justify-between px-4 py-3 bg-cyan-900/20 border-b border-cyan-500/20">
                <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-cyan-400/60" />
                    <div className="w-3 h-3 rounded-full bg-cyan-400/40" />
                    <div className="w-3 h-3 rounded-full bg-cyan-400/20" />
                </div>
                <span className="text-cyan-400 text-xs font-mono">Component.jsx</span>
                <div className="w-12" />
            </div>
            <div className="p-4 font-mono text-xs space-y-0.5 min-h-[160px]">
                {renderLines()}
            </div>
        </motion.div>
    );
};

// Response Card with live typing animation
export const ResponseCard = () => {
    const [cursorPos, setCursorPos] = useState(0);

    const codeLines = [
        '{',
        '  "success": true,',
        '  "message": "🚀 Launched!"',
        '}',
    ];

    const totalChars = codeLines.reduce((acc, line) => acc + line.length, 0);

    useEffect(() => {
        let charCount = 0;
        let timeout;

        const startDelay = setTimeout(() => {
            const animate = () => {
                charCount++;
                setCursorPos(charCount);

                if (charCount >= totalChars) {
                    timeout = setTimeout(() => {
                        charCount = 0;
                        setCursorPos(0);
                        animate();
                    }, 2000);
                } else {
                    timeout = setTimeout(animate, 60);
                }
            };
            animate();
        }, 4000);

        return () => {
            clearTimeout(startDelay);
            clearTimeout(timeout);
        };
    }, [totalChars]);

    const colorizeJSON = (char, fullLine, i) => {
        if (char === '{' || char === '}' || char === ':' || char === ',') return <span key={i} className="text-slate-400">{char}</span>;
        if (fullLine.includes('"success"') && fullLine.indexOf('success') <= i && i < fullLine.indexOf('success') + 9) return <span key={i} className="text-cyan-400">{char}</span>;
        if (fullLine.includes('"message"') && fullLine.indexOf('message') <= i && i < fullLine.indexOf('message') + 9) return <span key={i} className="text-cyan-400">{char}</span>;
        if (fullLine.includes('true') && fullLine.indexOf('true') <= i && i < fullLine.indexOf('true') + 4) return <span key={i} className="text-green-400">{char}</span>;
        if (char === '"') return <span key={i} className="text-orange-300">{char}</span>;
        if (fullLine.includes('Launched') || fullLine.includes('🚀')) return <span key={i} className="text-orange-300">{char}</span>;
        return <span key={i} className="text-slate-300">{char}</span>;
    };

    const renderLines = () => {
        let charsSoFar = 0;
        return codeLines.map((line, lineIdx) => {
            const lineStart = charsSoFar;
            const lineEnd = charsSoFar + line.length;
            charsSoFar = lineEnd;

            const cursorInLine = cursorPos > lineStart && cursorPos <= lineEnd;
            const cursorPosInLine = cursorPos - lineStart;
            const visibleText = cursorPos >= lineEnd ? line : line.slice(0, Math.max(0, cursorPosInLine));

            return (
                <div key={lineIdx} className={lineIdx === 1 || lineIdx === 2 ? 'pl-3' : ''}>
                    {visibleText.split('').map((char, i) => colorizeJSON(char, line, i))}
                    {cursorInLine && <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ duration: 0.5, repeat: Infinity }} className="text-green-400">|</motion.span>}
                </div>
            );
        });
    };

    return (
        <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            className="absolute top-[280px] right-[20px] w-[280px] bg-[#0d0d0f]/95 backdrop-blur-2xl border border-green-500/20 rounded-xl shadow-lg shadow-green-500/5 overflow-hidden"
        >
            <div className="flex items-center gap-2 px-3 py-2 bg-green-900/20 border-b border-green-500/20">
                <motion.div
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="w-2 h-2 rounded-full bg-green-400"
                />
                <span className="text-green-400 text-xs font-mono">Response</span>
            </div>
            <div className="p-3 font-mono text-xs min-h-[80px]">
                {renderLines()}
            </div>
        </motion.div>
    );
};

// Floating Tech Icons Component
const TechIcons = () => {
    // React SVG Icon
    const ReactIcon = () => (
        <svg viewBox="0 0 24 24" width="36" height="36" fill="#61DAFB">
            <circle cx="12" cy="12" r="2.5" />
            <ellipse cx="12" cy="12" rx="10" ry="4" fill="none" stroke="#61DAFB" strokeWidth="1" />
            <ellipse cx="12" cy="12" rx="10" ry="4" fill="none" stroke="#61DAFB" strokeWidth="1" transform="rotate(60 12 12)" />
            <ellipse cx="12" cy="12" rx="10" ry="4" fill="none" stroke="#61DAFB" strokeWidth="1" transform="rotate(120 12 12)" />
        </svg>
    );

    // Node.js SVG Icon
    const NodeIcon = () => (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="#339933">
            <path d="M12 21.985c-.275 0-.532-.074-.772-.202l-2.439-1.448c-.365-.203-.182-.277-.072-.314.496-.165.588-.201 1.101-.493.056-.037.129-.02.185.017l1.87 1.12c.074.036.166.036.221 0l7.319-4.237c.074-.036.11-.11.11-.202V7.768c0-.091-.036-.165-.11-.201l-7.319-4.219c-.073-.037-.165-.037-.221 0L4.552 7.566c-.073.036-.11.128-.11.201v8.457c0 .073.037.166.11.202l2 1.157c1.082.548 1.762-.095 1.762-.735V8.502c0-.11.091-.221.22-.221h.936c.108 0 .22.092.22.221v8.347c0 1.449-.788 2.294-2.164 2.294-.422 0-.752 0-1.688-.46l-1.925-1.099a1.55 1.55 0 0 1-.771-1.34V7.786c0-.55.293-1.064.771-1.339l7.316-4.237a1.637 1.637 0 0 1 1.544 0l7.317 4.237c.479.274.771.789.771 1.339v8.458c0 .549-.293 1.063-.771 1.34l-7.317 4.236c-.241.11-.516.165-.773.165z" />
        </svg>
    );

    // JavaScript Icon
    const JSIcon = () => (
        <svg viewBox="0 0 24 24" width="30" height="30">
            <rect width="24" height="24" rx="2" fill="#F7DF1E" />
            <path d="M6 15l1.5-1c.3.5.6.9 1.3.9.6 0 1-.3 1-1.1V8h2v5.8c0 1.8-1.1 2.7-2.7 2.7-1.4 0-2.3-.7-2.8-1.5zm6.5-.3l1.5-.9c.4.6.9 1.1 1.8 1.1.8 0 1.2-.4 1.2-.9 0-.6-.5-.9-1.4-1.3l-.5-.2c-1.4-.6-2.3-1.3-2.3-2.9 0-1.4 1.1-2.5 2.8-2.5 1.2 0 2.1.4 2.7 1.5l-1.5.9c-.3-.6-.7-.8-1.2-.8-.6 0-.9.4-.9.8 0 .6.4.8 1.2 1.2l.5.2c1.6.7 2.5 1.4 2.5 3 0 1.7-1.4 2.7-3.2 2.7-1.8 0-3-.8-3.5-1.9z" fill="#000" />
        </svg>
    );

    // TypeScript Icon
    const TSIcon = () => (
        <svg viewBox="0 0 24 24" width="30" height="30">
            <rect width="24" height="24" rx="2" fill="#3178C6" />
            <path d="M5 12v-1h7v1H9v6H7v-6H5zm9.5-1h5v1h-2v6h-2v-6h-1v-1z" fill="#fff" />
        </svg>
    );

    // Python Icon
    const PythonIcon = () => (
        <svg viewBox="0 0 24 24" width="32" height="32">
            <path fill="#3776AB" d="M12 2c-1.67 0-3.24.19-4.56.52C5.07 3.08 4 4.28 4 6v2.5c0 1.38 1.12 2.5 2.5 2.5h5V12H6c-1.68 0-3.15 1-3.8 2.44-.75 1.68-.75 3.44 0 5.12C2.85 21 4.32 22 6 22h2v-3.5c0-1.93 1.57-3.5 3.5-3.5h5c1.38 0 2.5-1.12 2.5-2.5V6c0-1.72-1.07-2.92-3.44-3.48C14.24 2.19 13.67 2 12 2zm-2.5 2a1 1 0 110 2 1 1 0 010-2z" />
            <path fill="#FFD43B" d="M12 22c1.67 0 3.24-.19 4.56-.52 2.37-.56 3.44-1.76 3.44-3.48v-2.5c0-1.38-1.12-2.5-2.5-2.5h-5V12h5.5c1.68 0 3.15-1 3.8-2.44.75-1.68.75-3.44 0-5.12C21.15 3 19.68 2 18 2h-2v3.5c0 1.93-1.57 3.5-3.5 3.5h-5C6.12 9 5 10.12 5 11.5V18c0 1.72 1.07 2.92 3.44 3.48 1.32.33 2.89.52 3.56.52zm2.5-2a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
    );

    // Firebase Icon
    const FirebaseIcon = () => (
        <svg viewBox="0 0 24 24" width="32" height="32">
            <path fill="#FFA000" d="M4 18L6.5 2 9.97 8.5 4 18z" />
            <path fill="#F57C00" d="M12 14l-4 4h13l-3-12-6 8z" />
            <path fill="#FFCA28" d="M12 8l-2.5 5.5L6 2l6 6zm0 0l4 12H5l7-12z" />
        </svg>
    );

    // MongoDB Icon
    const MongoIcon = () => (
        <svg viewBox="0 0 24 24" width="30" height="30" fill="#47A248">
            <path d="M12.5 2.1c-.1-.4-.5-.6-.5-.6s-.1.2-.2.5c-.3 1.2-1.8 2.6-2.5 4.5-.8 2.3-.7 4.4.4 6.3.4.7.6 1.4.6 2.3 0 .5-.2 1-.4 1.5l.1.1c.3.5.4 1.1.4 1.7 0 2.5-2.3 4.5-2.3 4.5s.7-.2 1.7-.7c2.5-1.3 5.6-4.1 5.6-8.7 0-3.3-1.4-6-2.9-8.5z" />
        </svg>
    );

    // GraphQL Icon
    const GraphQLIcon = () => (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="#E10098">
            <path d="M12.002 0a2.138 2.138 0 1 0 0 4.277 2.138 2.138 0 1 0 0-4.277zm8.54 4.931a2.138 2.138 0 1 0 0 4.277 2.138 2.138 0 1 0 0-4.277zm0 9.862a2.138 2.138 0 1 0 0 4.277 2.138 2.138 0 1 0 0-4.277zm-8.54 4.931a2.138 2.138 0 1 0 0 4.276 2.138 2.138 0 1 0 0-4.276zm-8.542-4.93a2.138 2.138 0 1 0 0 4.276 2.138 2.138 0 1 0 0-4.277zm0-9.863a2.138 2.138 0 1 0 0 4.277 2.138 2.138 0 1 0 0-4.277zm8.542-3.378L2.953 6.777v10.448l9.049 5.224 9.049-5.224V6.777zm0 1.601l7.66 13.27H4.34z" />
        </svg>
    );

    const icons = [
        // Top area icons
        { name: 'React', x: '5%', y: '5%', delay: 0, Icon: ReactIcon },
        { name: 'Node', x: '50%', y: '2%', delay: 0.5, Icon: NodeIcon },
        { name: 'JS', x: '85%', y: '8%', delay: 1, Icon: JSIcon },
        // Middle area icons
        { name: 'TS', x: '0%', y: '40%', delay: 1.5, Icon: TSIcon },
        { name: 'Python', x: '95%', y: '45%', delay: 2, Icon: PythonIcon },
        // Bottom area icons
        { name: 'Firebase', x: '10%', y: '85%', delay: 2.5, Icon: FirebaseIcon },
        { name: 'MongoDB', x: '50%', y: '90%', delay: 3, Icon: MongoIcon },
        { name: 'GraphQL', x: '85%', y: '80%', delay: 3.5, Icon: GraphQLIcon },
    ];

    return (
        <>
            {icons.map((icon, idx) => (
                <motion.div
                    key={icon.name}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                        opacity: [0.2, 0.4, 0.2],
                        scale: [0.9, 1.1, 0.9],
                        y: [0, -10, 0]
                    }}
                    transition={{
                        duration: 4 + idx * 0.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: icon.delay
                    }}
                    className="absolute pointer-events-none"
                    style={{ left: icon.x, top: icon.y }}
                >
                    <icon.Icon />
                </motion.div>
            ))}
        </>
    );
};

// Combined Floating Cards Component
const FloatingCodeCards = () => {
    return (
        <div className="relative h-[600px] hidden lg:block perspective-1000">
            {/* Background Tech Icons */}
            <TechIcons />

            {/* Floating Code Cards */}
            <ResponseCard />
            <ComponentCard />
            <TerminalCard />
        </div>
    );
};

export default FloatingCodeCards;
